/**
 * Stripe payment-acceptance helper used by submission pages (SubmissionDisplay)
 * after PaymentMethodForm creates a Payment Method. Calls
 * UserBountySubmissionAPI.acceptSubmission to create/confirm the PaymentIntent
 * server-side, then branches on PaymentStatus/StripePaymentStatus: immediate
 * success fetches the accepted submission; REQUIRES_ACTION/REQUIRES_CONFIRMATION
 * runs SCA/3DS via stripe.confirmCardPayment; pending states poll
 * getSubmissionPaymentStatus (by idempotency key) until terminal or timeout.
 * Returns { success, submission?, errorMessage? }; unit-tested in
 * app/__tests__/handlePaymentMethodCreated.test.ts.
 */
import { UserBountySubmissionAPI } from "@/api/userBountySubmission";
import { PaymentStatus, StripePaymentStatus } from "@/types/payment";
import { ProcessAcceptSubmissionPaymentResponse, SubmissionWithBounty } from "@/types/submission";
import type { Stripe } from "@stripe/stripe-js";

// Minimal surface needed from Stripe for this flow
type StripeConfirmOnly = Pick<Stripe, "confirmCardPayment">;

export interface HandlePaymentMethodCreatedParams {
    userId: number;
    submission: SubmissionWithBounty;
    paymentMethodId: string;
    stripePromise: Promise<StripeConfirmOnly | null>;
    intervalMs?: number;
    timeoutMs?: number;
}

export interface HandlePaymentMethodCreatedResult {
    success: boolean;
    submission?: SubmissionWithBounty;
    errorMessage?: string;
}

/**
 * Handle the Payment Method created by the payment form.
 *
 * Orchestrates the client ↔ server Stripe flow:
 * 1) Calls backend to accept the submission and process the payment (create + confirm PI).
 * 2) Interprets application-level payment status and underlying Stripe status.
 * 3) For PENDING results:
 *    - REQUIRES_PAYMENT_METHOD → show error (card declined / new method needed).
 *    - REQUIRES_ACTION / REQUIRES_CONFIRMATION → run SCA/3DS via `confirmCardPayment` (requires clientSecret).
 *    - PROCESSING / REQUIRES_CAPTURE → skip client confirmation and start polling.
 * 4) Polls payment status until terminal (succeeded/failed/cancelled). No acceptance polling.
 */
export async function handlePaymentMethodCreated(
    params: HandlePaymentMethodCreatedParams
): Promise<HandlePaymentMethodCreatedResult> {
    const {
        userId,
        submission,
        paymentMethodId,
        stripePromise,
        intervalMs = 1500,
        timeoutMs = 30000,
    } = params;

    type PollResult =
        | { kind: "terminal"; paymentStatus: PaymentStatus; paymentErrorMessage: string | null }
        | { kind: "timeout" };

    const pollPaymentStatusUntilTerminal = async (idempotencyKey: string): Promise<PollResult> => {
        const startedAt = Date.now();
        while (Date.now() - startedAt < timeoutMs) {
            try {
                const response = await UserBountySubmissionAPI.getSubmissionPaymentStatus(
                    userId,
                    submission.bountyId,
                    submission.id!,
                    idempotencyKey
                );
                if (response.paymentStatus !== PaymentStatus.PENDING) {
                    return {
                        kind: "terminal",
                        paymentStatus: response.paymentStatus,
                        paymentErrorMessage: response.paymentErrorMessage ?? null,
                    };
                }
            } catch (_) {
                // ignore and continue polling
            }
            await new Promise((r) => setTimeout(r, intervalMs));
        }
        return { kind: "timeout" };
    };

    const fetchAcceptedSubmissionOnce = async (): Promise<SubmissionWithBounty | null> => {
        try {
            const { submission: acceptedSubmission } =
                await UserBountySubmissionAPI.getAcceptedSubmission(userId, submission.bountyId);
            return acceptedSubmission ? { ...acceptedSubmission, bounty: submission.bounty } : null;
        } catch (_) {
            return null;
        }
    };

    const handlePollOutcome = async (
        pollResult: PollResult
    ): Promise<HandlePaymentMethodCreatedResult> => {
        if (pollResult.kind === "timeout") {
            return {
                success: false,
                errorMessage:
                    "timed out waiting for payment confirmation. please refresh in a moment.",
            };
        }

        if (pollResult.kind === "terminal") {
            const status = pollResult.paymentStatus;
            if (status === PaymentStatus.SUCCEEDED) {
                const accepted = await fetchAcceptedSubmissionOnce();
                if (accepted) {
                    return { success: true, submission: accepted };
                }
                return { success: false, errorMessage: "failed to fetch accepted submission" };
            }
            if (status === PaymentStatus.CANCELLED) {
                return { success: false, errorMessage: "payment was cancelled" };
            }
            if (status === PaymentStatus.FAILED) {
                return {
                    success: false,
                    errorMessage: pollResult.paymentErrorMessage || "payment failed",
                };
            }
        }
        return { success: false, errorMessage: "unexpected payment status" };
    };

    try {
        // Step 1: Ask backend to accept submission and process payment
        const result: ProcessAcceptSubmissionPaymentResponse =
            await UserBountySubmissionAPI.acceptSubmission(
                userId,
                submission.bountyId,
                submission.id!,
                paymentMethodId
            );

        const paymentStatus: PaymentStatus = result.paymentStatus;

        // SUCCEEDED: fetch accepted submission once to populate item link
        if (paymentStatus === PaymentStatus.SUCCEEDED) {
            try {
                const { submission: acceptedSubmission } =
                    await UserBountySubmissionAPI.getAcceptedSubmission(
                        userId,
                        submission.bountyId
                    );
                if (acceptedSubmission) {
                    return {
                        success: true,
                        submission: { ...acceptedSubmission, bounty: submission.bounty },
                    };
                }
            } catch (_) {
                // ignore
            }
            // This probably shouldn't happen, but we'll handle it gracefully.
            return { success: false, errorMessage: "failed to fetch accepted submission" };
        } else if (paymentStatus === PaymentStatus.CANCELLED) {
            return { success: false, errorMessage: "payment was cancelled" };
        } else if (paymentStatus === PaymentStatus.FAILED) {
            return {
                success: false,
                errorMessage: result.paymentDetails.paymentErrorMessage || "payment failed",
            };
        }
        // PENDING: may require SCA/3DS
        else if (paymentStatus === PaymentStatus.PENDING) {
            // If Stripe requires a new payment method, show error immediately.
            const rawStripeStatus: StripePaymentStatus = result.paymentDetails.stripePaymentStatus;

            if (rawStripeStatus === StripePaymentStatus.REQUIRES_PAYMENT_METHOD) {
                return {
                    success: false,
                    errorMessage:
                        "your card was declined or requires a new payment method. please try another card.",
                };
            }

            // If SCA/3DS is required, we must confirm using client secret.
            if (
                rawStripeStatus === StripePaymentStatus.REQUIRES_ACTION ||
                rawStripeStatus === StripePaymentStatus.REQUIRES_CONFIRMATION
            ) {
                const clientSecret = result.paymentDetails.clientSecret;
                const stripe = await stripePromise;
                if (!stripe || !clientSecret) {
                    return {
                        success: false,
                        errorMessage:
                            "payment requires additional authentication, but no client secret was returned",
                    };
                }

                const scaResult = await stripe.confirmCardPayment(clientSecret);
                if (scaResult?.error) {
                    return {
                        success: false,
                        errorMessage: scaResult.error.message || "authentication failed",
                    };
                }

                // After successful 3DS, poll payment status until it leaves PENDING due to webhook hit on backend.
                const pollResult = await pollPaymentStatusUntilTerminal(
                    result.paymentDetails.paymentIdempotencyKey
                );
                return await handlePollOutcome(pollResult);
            }

            // For other pending statuses (e.g., processing, requires_capture), poll payment status first.
            const pollResult = await pollPaymentStatusUntilTerminal(
                result.paymentDetails.paymentIdempotencyKey
            );
            return await handlePollOutcome(pollResult);
        }

        return { success: false, errorMessage: "unexpected payment status" };
    } catch (error) {
        // Let callers decide whether to rethrow; we surface a user-friendly message first
        // eslint-disable-next-line no-console
        console.error("Failed to accept submission:", error);
        throw error;
    }
}
